import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { Loader2, Search, Building2, ArrowLeft, Handshake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ServiceAreaPicker } from "@/components/ServiceAreaPicker";
import { BrandLogo } from "@/components/BrandLogo";

const SPECIALS = ["Condominiums", "House & Lot", "Luxury", "Commercial", "Rentals", "Investments", "Pre-selling"];

function BrandLink() {
  return (
    <Link to="/login" className="flex items-center gap-2.5 mb-6">
      <BrandLogo className="h-[80px]" />
    </Link>
  );
}

function ChoiceScreen({ onPick }) {
  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-3xl mx-auto pc-reveal">
        <BrandLink />
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">How will you use PropertyConnect?</h1>
        <p className="text-slate-500 mt-1">Choose the option that best describes you.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-7">
          <button data-testid="choice-consumer" onClick={() => onPick("consumer")}
            className="text-left bg-white border-2 border-border rounded-2xl p-6 hover:border-accent hover:shadow-md transition-all group">
            <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center"><Search className="h-6 w-6 text-accent" /></div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-4">Property Seeker or Owner</h3>
            <p className="text-sm text-slate-500 mt-1">Looking to buy, rent or stay—or want professional help with your property.</p>
          </button>
          <button data-testid="choice-professional" onClick={() => onPick("professional")}
            className="text-left bg-white border-2 border-border rounded-2xl p-6 hover:border-primary hover:shadow-md transition-all group">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center"><Building2 className="h-6 w-6 text-primary" /></div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-4">Real Estate Professional</h3>
            <p className="text-sm text-slate-500 mt-1">For brokers, salespersons, property managers and other real-estate professionals.</p>
          </button>
          <button data-testid="choice-referral" onClick={() => onPick("referral")}
            className="text-left bg-white border-2 border-border rounded-2xl p-6 hover:border-[#C79A3B] hover:shadow-md transition-all group">
            <div className="h-12 w-12 rounded-xl bg-[#C79A3B]/10 flex items-center justify-center"><Handshake className="h-6 w-6 text-[#C79A3B]" /></div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-4">Referral Partner</h3>
            <p className="text-sm text-slate-500 mt-1">Refer a buyer, renter or property owner to a participating real estate professional.</p>
          </button>
        </div>
        <p className="text-sm text-slate-500 mt-6">Already have an account? <Link to="/login" className="text-accent font-semibold">Sign in</Link></p>
      </div>
    </div>
  );
}

function ConsumerForm({ onBack }) {
  const { registerConsumer } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ full_name: "", email: "", password: "", confirm_password: "", accept_terms: false, accept_privacy: false, invite_code: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.full_name.trim()) { setErr("Please enter your full name."); return; }
    if (f.password !== f.confirm_password) { setErr("Passwords do not match."); return; }
    if (f.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (!f.accept_terms || !f.accept_privacy) { setErr("Please accept the terms and privacy policy."); return; }
    setBusy(true);
    try {
      await registerConsumer(f);
      nav("/", { replace: true });
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-md mx-auto pc-reveal">
        <BrandLink />
        <button data-testid="consumer-back" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Create your account</h1>
        <p className="text-slate-500 mt-1">Looking to buy, rent or stay—or want professional help with your property.</p>

        <form onSubmit={submit} className="mt-7 bg-slate-50 border border-border rounded-2xl p-6 sm:p-8 space-y-4" data-testid="consumer-register-form">
          <div><Label>Full name</Label><Input data-testid="consumer-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email</Label><Input data-testid="consumer-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Password</Label><Input data-testid="consumer-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Confirm password</Label><Input data-testid="consumer-confirm" type="password" value={f.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} className="mt-1.5" /></div>
          <div className="space-y-2.5 pt-1">
            {[
              ["accept_terms", "Terms of Service", "/terms"],
              ["accept_privacy", "Privacy Policy", "/privacy"],
            ].map(([k, name, href]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer">
                <Checkbox data-testid={`consumer-${k}`} checked={f[k]} onCheckedChange={(v) => set(k, !!v)} />
                <span>I accept the <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`consumer-${k}-link`} className="text-accent font-semibold hover:underline" onClick={(e) => e.stopPropagation()}>{name}</a></span>
              </label>
            ))}
          </div>
          {err && <p data-testid="consumer-register-error" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
          <Button data-testid="consumer-register-submit" type="submit" disabled={busy} className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-xl">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
          </Button>
          <p className="text-sm text-slate-500 text-center">Already have an account? <Link to="/login" className="text-accent font-semibold">Sign in</Link></p>
        </form>
      </div>
    </div>
  );
}

function ProfessionalForm({ onBack }) {
  const { register } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", mobile: "", password: "", confirm_password: "",
    role: "SALESPERSON", organization_type: "", organization_name: "", service_areas: [], brokerage: "", specializations: [], prc_number: "", broker_affiliation: "",
    accept_terms: false, accept_privacy: false, confirm_accurate: false, invite_code: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSpec = (v) => setF((p) => ({ ...p, specializations: p.specializations.includes(v) ? p.specializations.filter((x) => x !== v) : [...p.specializations, v] }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password !== f.confirm_password) { setErr("Passwords do not match."); return; }
    if (f.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (!f.accept_terms || !f.accept_privacy || !f.confirm_accurate) { setErr("Please accept all required agreements."); return; }
    if (!f.service_areas || f.service_areas.length === 0) { setErr("Please add at least one service area."); return; }
    setBusy(true);
    try {
      sessionStorage.setItem("pc_just_registered", "1");
      await register(f);
      nav("/profile", { replace: true });
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-2xl mx-auto pc-reveal">
        <BrandLink />
        <button data-testid="professional-back" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Create your professional account</h1>
        <p className="text-slate-500 mt-1">Join the professional real estate network connecting properties, Buyer Needs and opportunities.</p>

        <form onSubmit={submit} className="mt-7 bg-slate-50 border border-border rounded-2xl p-6 sm:p-8 grid sm:grid-cols-2 gap-4" data-testid="register-form">
          <div><Label>First name</Label><Input data-testid="reg-first" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Last name</Label><Input data-testid="reg-last" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email</Label><Input data-testid="reg-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Mobile number</Label><Input data-testid="reg-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+63 9XX XXX XXXX" className="mt-1.5" /></div>
          <div><Label>Password</Label><Input data-testid="reg-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Confirm password</Label><Input data-testid="reg-confirm" type="password" value={f.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} className="mt-1.5" /></div>

          <div>
            <Label>Professional role</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="reg-role" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="LICENSED_BROKER">Licensed Real Estate Broker</SelectItem>
                <SelectItem value="SALESPERSON">Real Estate Salesperson</SelectItem>
                <SelectItem value="PROPERTY_MANAGER">Property Manager</SelectItem>
                <SelectItem value="PROPERTY_OWNER">Property Owner</SelectItem>
                <SelectItem value="DEVELOPER_REP">Developer Representative</SelectItem>
                <SelectItem value="OTHER">Other Real Estate Professional</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Service areas</Label>
            <p className="text-sm text-slate-500 mt-0.5">Select the areas where you primarily handle properties and clients.</p>
            <ServiceAreaPicker value={f.service_areas} onChange={(v) => set("service_areas", v)} />
          </div>

          <div>
            <Label>Organization type <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Select value={f.organization_type || undefined} onValueChange={(v) => set("organization_type", v)}>
              <SelectTrigger data-testid="reg-org-type" className="mt-1.5"><SelectValue placeholder="Select organization type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INDEPENDENT">Independent Practitioner</SelectItem>
                <SelectItem value="BROKERAGE">Realty Brokerage</SelectItem>
                <SelectItem value="DEVELOPER">Property Developer</SelectItem>
                <SelectItem value="PROPERTY_MGMT">Property Management Company</SelectItem>
                <SelectItem value="OTHER_ORG">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Company / Organization <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="reg-org-name" value={f.organization_name} onChange={(e) => set("organization_name", e.target.value)} placeholder="e.g. ABC Realty" className="mt-1.5" /></div>

          <div><Label>PRC number {f.role === "LICENSED_BROKER" ? "" : "(if applicable)"}</Label><Input data-testid="reg-prc" value={f.prc_number} onChange={(e) => set("prc_number", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Broker affiliation {f.role === "SALESPERSON" ? "(your broker)" : ""}</Label><Input data-testid="reg-affiliation" value={f.broker_affiliation} onChange={(e) => set("broker_affiliation", e.target.value)} className="mt-1.5" /></div>

          <div className="sm:col-span-2">
            <Label>Specializations</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SPECIALS.map((s) => (
                <button type="button" key={s} onClick={() => toggleSpec(s)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${f.specializations.includes(s) ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-slate-600"}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2 space-y-2.5 pt-2">
            {[
              ["accept_terms", "I accept the ", "Terms of Service", "/terms"],
              ["accept_privacy", "I accept the ", "Privacy Policy", "/privacy"],
              ["confirm_accurate", "I confirm the information I submitted is accurate", null, null],
            ].map(([k, pre, name, href]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer">
                <Checkbox data-testid={`reg-${k}`} checked={f[k]} onCheckedChange={(v) => set(k, !!v)} />
                <span>{pre}{href ? <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`reg-${k}-link`} className="text-accent font-semibold hover:underline" onClick={(e) => e.stopPropagation()}>{name}</a> : name}</span>
              </label>
            ))}
          </div>

          {err && <p data-testid="register-error" className="sm:col-span-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}

          <div className="sm:col-span-2">
            <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-xl">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
            </Button>
            <p className="text-sm text-slate-500 mt-4 text-center">Already have an account? <Link to="/login" className="text-accent font-semibold">Sign in</Link></p>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReferralPartnerForm({ onBack }) {
  const { registerReferralPartner } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ full_name: "", email: "", mobile: "", password: "", confirm_password: "", accept_terms: false, accept_privacy: false, invite_code: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.full_name.trim()) { setErr("Please enter your full name."); return; }
    if (f.password !== f.confirm_password) { setErr("Passwords do not match."); return; }
    if (f.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (!f.accept_terms || !f.accept_privacy) { setErr("Please accept the terms and privacy policy."); return; }
    setBusy(true);
    try {
      await registerReferralPartner(f);
      nav("/referrals", { replace: true });
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-md mx-auto pc-reveal">
        <BrandLink />
        <button data-testid="referral-back" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Become a Referral Partner</h1>
        <p className="text-slate-500 mt-1">Refer a buyer, renter or property owner to a participating real estate professional.</p>

        <form onSubmit={submit} className="mt-7 bg-slate-50 border border-border rounded-2xl p-6 sm:p-8 space-y-4" data-testid="referral-register-form">
          <div><Label>Full name</Label><Input data-testid="referral-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email</Label><Input data-testid="referral-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Mobile number <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="referral-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+63 9XX XXX XXXX" className="mt-1.5" /></div>
          <div><Label>Password</Label><Input data-testid="referral-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Confirm password</Label><Input data-testid="referral-confirm" type="password" value={f.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} className="mt-1.5" /></div>
          <div className="space-y-2.5 pt-1">
            {[
              ["accept_terms", "Terms of Service", "/terms"],
              ["accept_privacy", "Privacy Policy", "/privacy"],
            ].map(([k, name, href]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer">
                <Checkbox data-testid={`referral-${k}`} checked={f[k]} onCheckedChange={(v) => set(k, !!v)} />
                <span>I accept the <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent font-semibold hover:underline" onClick={(e) => e.stopPropagation()}>{name}</a></span>
              </label>
            ))}
          </div>
          {err && <p data-testid="referral-register-error" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
          <Button data-testid="referral-register-submit" type="submit" disabled={busy} className="w-full h-11 bg-[#C79A3B] text-[#0F2C59] hover:bg-[#C79A3B]/90 font-semibold rounded-xl">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
          </Button>
          <p className="text-sm text-slate-500 text-center">Already have an account? <Link to="/login" className="text-accent font-semibold">Sign in</Link></p>
        </form>
      </div>
    </div>
  );
}

export default function Register() {
  const [mode, setMode] = useState("choice"); // choice | consumer | professional | referral
  const screen = mode === "consumer"
    ? <ConsumerForm onBack={() => setMode("choice")} />
    : mode === "professional"
      ? <ProfessionalForm onBack={() => setMode("choice")} />
      : mode === "referral"
        ? <ReferralPartnerForm onBack={() => setMode("choice")} />
        : <ChoiceScreen onPick={setMode} />;
  return (
    <>
      {screen}
      <div className="bg-background px-4 pb-8"><SiteFooter variant="minimal" /></div>
    </>
  );
}
